
import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Database,
  Share2,
  Lock,
  UserCheck,
  RefreshCcw,
  AlertTriangle,
  FileText,
} from "lucide-react";

/* ---------- Reusable Policy Card ---------- */
const PolicyItem = ({ icon: Icon, title, color, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.35 }}
    className="bg-slate-50 p-5 rounded-2xl border border-slate-100"
  >
    <h2 className="flex items-center text-sm font-bold text-slate-800 mb-3 gap-2">
      <Icon className={`w-5 h-5 ${color} shrink-0`} />
      {title}
    </h2>
    <div className="text-xs text-slate-600 leading-relaxed space-y-2">
      {children}
    </div>
  </motion.section>
);

/* ---------- Main Component ---------- */
const PrivacySection = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc] font-outfit relative pb-20">

      {/* Dark Hero Background for Navbar Visibility */}
      <div className="absolute top-0 left-0 w-full h-[180px] bg-[#020617] z-0 overflow-hidden rounded-b-[2rem] shadow-xl">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" alt="cover" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/50 to-[#020617]"></div>
      </div>

      {/* Container */}
      <div className="relative z-10 pt-24 max-w-7xl w-full mx-auto px-4 md:px-6">

        {/* Breadcrumbs */}
        <div className="text-white text-xs font-bold uppercase tracking-widest mb-6 opacity-80 flex items-center gap-2">
          <a href="/home" className="hover:text-blue-300 transition-colors">Home</a>
          <span>/</span>
          <span>Privacy Policy</span>
        </div>

        {/* White Canvas */}
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6 md:p-10 overflow-hidden">

          {/* Compact Header */}
          <div className="flex items-start gap-4 mb-8 pb-6 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Privacy Policy</h2>
              <p className="text-xs text-slate-500 mt-0.5">EasyMarg — How we collect, use, and protect your data.</p>
            </div>
          </div>

          {/* Privacy Policy Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

            <PolicyItem icon={Database} title="1. Information We Collect" color="text-blue-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Name, email, phone number</li>
                <li>Business details</li>
                <li>Service requirements</li>
                <li>Communication metadata</li>
                <li>Technical data (IP address, browser, device)</li>
              </ul>
              <p>We do <strong>not collect sensitive personal data</strong> unless explicitly required and consented.</p>
            </PolicyItem>

            <PolicyItem icon={UserCheck} title="2. Use of Information" color="text-indigo-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Facilitate professional connections</li>
                <li>Improve platform functionality</li>
                <li>Ensure security and fraud prevention</li>
                <li>Meet legal and regulatory obligations</li>
              </ul>
            </PolicyItem>

            <PolicyItem icon={Share2} title="3. Data Sharing" color="text-teal-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>With professionals you choose to engage</li>
                <li>With service providers for platform operations</li>
                <li>When legally required by authorities</li>
              </ul>
              <p>We <strong>do not sell personal data</strong> to third parties.</p>
            </PolicyItem>

            <PolicyItem icon={Lock} title="4. Confidentiality" color="text-purple-600">
              <p>EasyMarg does not access professional-client privileged communications.</p>
              <p>Confidentiality obligations rest with the professional as per applicable laws.</p>
            </PolicyItem>

            <PolicyItem icon={ShieldCheck} title="5. Data Security" color="text-green-600">
              <p>We use reasonable technical and organizational safeguards to protect data.</p>
              <p>However, no digital platform can guarantee absolute security.</p>
            </PolicyItem>

            <PolicyItem icon={RefreshCcw} title="6. User Rights" color="text-orange-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Request access or correction of data</li>
                <li>Withdraw consent where applicable</li>
                <li>Request account deletion (subject to legal retention requirements)</li>
              </ul>
            </PolicyItem>

          </div>

          {/* Cancellation & Refund */}
          <div className="border-t border-slate-100 pt-8 mb-8">
            <h3 className="text-base font-bold text-slate-800 mb-6">Cancellation &amp; Refund Policy</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PolicyItem icon={FileText} title="1. Platform Fees" color="text-blue-600">
                <p>If EasyMarg charges a platform or facilitation fee, cancellation terms will be clearly displayed at the time of payment.</p>
                <ul className="list-disc list-inside space-y-1.5">
                  <li>Refunds depend on service status</li>
                  <li>Fees may be partially or fully non-refundable once engagement begins</li>
                </ul>
              </PolicyItem>
              <PolicyItem icon={AlertTriangle} title="2. Professional Fees &amp; Disputes" color="text-red-600">
                <ul className="list-disc list-inside space-y-1.5">
                  <li>Professional fees are governed by agreements with the professional</li>
                  <li>EasyMarg has no authority to enforce professional refunds</li>
                  <li>Disputes must be resolved directly between users and professionals</li>
                </ul>
                <p>EasyMarg may assist communication but is <strong>not a dispute-resolution authority</strong>.</p>
              </PolicyItem>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-amber-900 mb-3">Disclaimer</h3>
            <ul className="list-disc list-inside space-y-1.5 text-xs text-amber-800">
              <li>EasyMarg is an intermediary platform under applicable Indian IT laws</li>
              <li>Platform information is for general facilitation purposes only</li>
              <li>Professional outcomes depend on facts, circumstances, and judgment</li>
              <li>Use of EasyMarg does not create a professional-client relationship with EasyMarg</li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacySection;
